import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { guardianApi } from '../../services/api/guardian';
import { Card, H1, Text, Button } from '../../components/ui';
import { Input } from '../../components/forms';
import { useToast } from '../../components/ui';

const passwordSchema = z.object({
  email: z.string().email('Valid email is required'),
});

const resetConfirmSchema = z.object({
  token: z.string().min(1, 'Reset token is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirm_password: z.string(),
}).refine(data => data.password === data.confirm_password, {
  message: 'Passwords do not match',
  path: ['confirm_password'],
});

type RequestFormData = z.infer<typeof passwordSchema>;
type ConfirmFormData = z.infer<typeof resetConfirmSchema>;

export const GuardianPasswordReset: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [step, setStep] = useState<'request' | 'confirm' | 'done'>('request');
  const [showPassword, setShowPassword] = useState(false);

  const requestForm = useForm<RequestFormData>({
    resolver: zodResolver(passwordSchema),
  });

  const confirmForm = useForm<ConfirmFormData>({
    resolver: zodResolver(resetConfirmSchema),
  });

  const requestMutation = useMutation({
    mutationFn: (data: RequestFormData) => guardianApi.auth.requestPasswordReset(data.email),
    onSuccess: () => {
      setStep('confirm');
      addToast({ title: 'Email Sent', message: 'Check your email for the reset link', type: 'success' });
    },
    onError: () => {
      addToast({ title: 'Error', message: 'Failed to send reset email', type: 'error' });
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (data: ConfirmFormData) =>
      guardianApi.auth.confirmPasswordReset(data.token, data.password),
    onSuccess: () => {
      setStep('done');
    },
    onError: () => {
      addToast({ title: 'Error', message: 'Invalid or expired reset token', type: 'error' });
    },
  });

  if (step === 'done') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center px-4">
        <Card className="max-w-md w-full p-8 text-center">
          <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500 mb-4" />
          <H1 className="mb-2">Password Reset</H1>
          <Text className="text-gray-600 mb-6">
            Your password has been reset successfully. You can now log in with your new password.
          </Text>
          <Button variant="primary" onClick={() => navigate('/guardian/login')}>
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center px-4">
      <Card className="max-w-md w-full p-8">
        <H1 className="text-center mb-2">Reset Password</H1>
        <Text className="text-center text-gray-600 mb-6">
          {step === 'request'
            ? 'Enter your email to receive a password reset link'
            : 'Enter the reset token from your email and your new password'}
        </Text>

        {step === 'request' ? (
          <form onSubmit={requestForm.handleSubmit((data) => requestMutation.mutate(data))} className="space-y-4">
            <Input
              {...requestForm.register('email')}
              label="Email Address"
              type="email"
              placeholder="your@email.com"
              error={requestForm.formState.errors.email?.message}
            />
            <Button
              type="submit"
              variant="primary"
              fullWidth
              isLoading={requestMutation.isPending}
            >
              Send Reset Link
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/guardian/login')}
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                Back to Login
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={confirmForm.handleSubmit((data) => confirmMutation.mutate(data))} className="space-y-4">
            <Input
              {...confirmForm.register('token')}
              label="Reset Token"
              placeholder="Paste the token from your email"
              error={confirmForm.formState.errors.token?.message}
            />
            <div className="relative">
              <Input
                {...confirmForm.register('password')}
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                error={confirmForm.formState.errors.password?.message}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
              </button>
            </div>
            <Input
              {...confirmForm.register('confirm_password')}
              label="Confirm Password"
              type="password"
              error={confirmForm.formState.errors.confirm_password?.message}
            />
            <Button
              type="submit"
              variant="primary"
              fullWidth
              isLoading={confirmMutation.isPending}
            >
              Reset Password
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => setStep('request')}
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                Resend reset email
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};
